<script setup>
import moment from "moment";
import { computed } from "vue";
import { useHomeContentStore } from "@/app/stores/homeContentStore";

// eslint-disable-next-line no-undef
const emit = defineEmits(["closeDialog"]);

const baseUrl = process.env.VUE_APP_BASE_URI;
const homeContentStore = useHomeContentStore();
const benefit = computed(() => homeContentStore.benefit);
const benefitImageUrl = computed(() => {
  const mediaObjects = benefit.value?.benefitMediaObjects || [];
  const contentUrl = mediaObjects.length > 0 ? mediaObjects[0].contentUrl : null;

  return contentUrl ? `${baseUrl}${contentUrl}` : "";
});

const formatDateTime = (value) => {
  if (value) {
    return moment(value).format("DD/MM/YYYY");
  }
  return "";
}

const formatDescription = (value) => {
  if (value) {
    return value.replace(/\n/g, "<br />");
  }
  return "";
}

const closeDialog = () => {
  emit('closeDialog');
}
</script>

<template>
  <div class="benefit-dialog-content">
    <div class="header-new">
      <svg class="asterisk asterisk-yellow" xmlns="http://www.w3.org/2000/svg" width="724" height="1024"
        viewBox="0 0 724 1024">
        <path
          d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
      </svg>
      <img class="asterisk-red asterisk-red-top" src="@/assets/images/aspa_roja_small_entrada.png" alt="">
      <img class="asterisk-red asterisk-red-side" src="@/assets/images/aspa_roja_small_entrada.png" alt="">
      <svg class="asterisk asterisk-black" xmlns="http://www.w3.org/2000/svg" width="724" height="233"
        viewBox="0 0 724 1024">
        <path
          d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
      </svg>
      <img class="bgtishert" src="@/assets/images/titulo-benefit.png" alt="">
      <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt="" @click="closeDialog">
    </div>
    <div v-if="benefit" class="body-new">
      <div class="benefit-summary">
        <el-image lazy class="image-thumbaild" :src="benefitImageUrl" fit="cover">
          <template v-slot:error>
            <div class="image-fallback">
              <img src="@/assets/images/picture-loading-failed.png" alt="">
            </div>
          </template>
        </el-image>
        <div class="benefit-info">
          <h1 class="active-value font-weight-bold">
            {{ benefit.active ? 'Activo' : 'No activo' }}
          </h1>
          <h4 class="time font-weight-bold">
            Del {{ formatDateTime(benefit.fromDate) }} hasta {{ formatDateTime(benefit.toDate) }}
          </h4>
          <h1 class="title font-weight-bold">
            {{ benefit.title }}
          </h1>
          <h2 v-if="benefit.contactName || benefit.contactPhone" class="subtitle font-weight-bold">
            {{ benefit.contactName }} {{ benefit.contactPhone }}
          </h2>
        </div>
      </div>
      <div v-if="benefit.description" class="benefit-description" v-html="formatDescription(benefit.description)" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.subtitle {
  color: #f7d20e;
  font-size: 24px;
  line-height: 1.2;
  margin: 0;
  word-break: break-word;
}

.title {
  color: #f9166f;
  font-size: 36px;
  line-height: 1.25;
  margin: 18px 0 10px;
  word-break: break-word;
}

.active-value {
  color: #f7d20e;
  font-size: 36px;
  line-height: 1;
  margin: 0 0 28px;
}

.time {
  font-size: 18px;
  color: black;
  line-height: 1.35;
  margin: 0;
}

.benefit-dialog-content {
  background: #f3f0fb;
  overflow-x: hidden;
  position: relative;
}

.header-new {
  min-height: 245px;
  position: relative;
  z-index: 10;
}

.body-new {
  position: relative;
  padding: 0 50px 6px;
  z-index: 1;
}

.benefit-summary {
  align-items: flex-start;
  display: flex;
  gap: 32px;
}

.benefit-info {
  flex: 1;
  min-width: 0;
}

.benefit-description {
  color: black;
  font-size: 16px;
  line-height: 1.55;
  margin-top: 28px;
  text-align: justify;
  overflow-wrap: anywhere;
  word-break: normal;
}

.image-thumbaild {
  flex: 0 0 260px;
  height: 260px;
  max-width: 260px;
  width: 260px;
}

.image-fallback,
.image-fallback img {
  height: 100%;
  width: 100%;
}

.image-fallback img {
  object-fit: contain;
}

.asterisk-yellow {
  position: absolute;
  top: -460px;
  left: 20px;
  fill: #f7d20e;
  width: 4%;
}

.asterisk-red-top {
  position: absolute;
  top: 42px;
  right: 280px;
  width: 3%;
}

.asterisk-red-side {
  position: absolute;
  top: 300px;
  right: 80px;
  width: 4%;
}

.asterisk-black {
  position: absolute;
  top: 120px;
  right: 270px;
  width: 3%;
}

.bgtishert {
  max-width: 640px;
  position: absolute;
  top: 95px;
  left: 50px;
  width: calc(100% - 230px);
}

.close-icon {
  width: 30px !important;
  height: 30px !important;
  position: absolute;
  right: 20px;
  top: 10px;
  z-index: 11;
}

@media (max-width: 900px) {
  .header-new {
    min-height: 210px;
  }

  .body-new {
    padding: 0 32px 16px;
  }

  .benefit-summary {
    gap: 24px;
  }

  .image-thumbaild {
    flex-basis: 220px;
    height: 220px;
    max-width: 220px;
    width: 220px;
  }

  .active-value {
    font-size: 32px;
    margin-bottom: 20px;
  }

  .title {
    font-size: 32px;
  }

  .subtitle {
    font-size: 22px;
  }

  .bgtishert {
    left: 32px;
    top: 86px;
    width: calc(100% - 160px);
  }
}

@media (max-width: 767px) {
  .header-new {
    min-height: 150px;
  }

  .body-new {
    padding: 0 18px 28px;
  }

  .benefit-summary {
    flex-direction: column;
    gap: 20px;
  }

  .image-thumbaild {
    flex-basis: auto;
    height: auto;
    max-height: 320px;
    max-width: 100%;
    width: 100%;
  }

  .active-value {
    font-size: 30px;
    margin-bottom: 16px;
  }

  .title {
    font-size: 28px;
  }

  .subtitle {
    font-size: 20px;
  }

  .bgtishert {
    left: 18px;
    top: 70px;
    width: calc(100% - 72px);
  }

  .asterisk-yellow {
    left: 12px;
    top: -430px;
    width: 9%;
  }

  .asterisk-red-top,
  .asterisk-red-side,
  .asterisk-black {
    display: none;
  }
}
</style>
